import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { usePortal } from './PortalContext';

// Initialize Stripe only when needed (lazy load)
let stripePromise = null;
const getStripe = () => {
  if (!stripePromise && process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY) {
    stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
  }
  return stripePromise;
};

const BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

// Checkout Form Component for Stripe
const CheckoutForm = ({ invoice, clientSecret, onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsLoading(true);
    setErrorMessage(null);

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: {
        return_url: `${window.location.origin}/portal/invoices/${invoice.id}`,
      },
      redirect: 'if_required'
    });

    if (error) {
      setErrorMessage(error.message);
      setIsLoading(false);
    } else if (paymentIntent && paymentIntent.status === 'succeeded') {
      // Payment succeeded without redirect
      onSuccess(paymentIntent.id);
    } else {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} style={styles.paymentForm}>
      <PaymentElement options={{ layout: 'tabs' }} />

      {errorMessage && (
        <div style={styles.paymentError}>{errorMessage}</div>
      )}

      <div style={styles.paymentButtons}>
        <button
          type="button"
          onClick={onCancel}
          style={styles.cancelButton}
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || isLoading}
          style={{
            ...styles.payButton,
            ...(isLoading || !stripe ? styles.payButtonDisabled : {})
          }}
        >
          {isLoading ? (
            <>
              <span style={styles.buttonSpinner}></span>
              Processing...
            </>
          ) : (
            `Pay ${formatCurrency(invoice.total || invoice.amount)}`
          )}
        </button>
      </div>
    </form>
  );
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD'
  }).format(amount || 0);
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  return new Date(dateString).toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });
};

const PortalInvoiceDetail = () => {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, fetchInvoice, confirmPayment, createPaymentIntent, logout } = usePortal();

  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPayment, setShowPayment] = useState(searchParams.get('pay') === 'true');
  const [clientSecret, setClientSecret] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  useEffect(() => {
    if (isAuthenticated) {
      loadInvoice();
    }
  }, [isAuthenticated, id]);

  const loadInvoice = async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fetchInvoice(id);
      setInvoice(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentClick = async () => {
    if (!invoice || invoice.status === 'paid') return;

    try {
      setLoading(true);
      const { clientSecret: secret } = await createPaymentIntent(invoice.id);
      setClientSecret(secret);
      setShowPayment(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = async (paymentIntentId) => {
    try {
      setLoading(true);
      await confirmPayment(invoice.id, paymentIntentId);
      setPaymentSuccess(true);
      setShowPayment(false);
      // Refresh invoice data
      await loadInvoice();
    } catch (err) {
      setError('Payment succeeded but we could not update the invoice. Please contact support.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return '#22c55e';
      case 'overdue':
        return '#ef4444';
      case 'pending':
      case 'sent':
        return '#f59e0b';
      default:
        return '#6b7280';
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div style={styles.container}>
        <nav style={styles.navbar}>
          <div style={styles.navContent}>
            <div style={styles.navLeft}>
              <div style={styles.navLogo}>🔧</div>
              <h1 style={styles.navTitle}>Customer Portal</h1>
            </div>
          </div>
        </nav>
        <main style={styles.main}>
          <div style={styles.loadingState}>
            <div style={styles.spinner}></div>
            <p>Loading...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div style={styles.container}>
        <nav style={styles.navbar}>
          <div style={styles.navContent}>
            <div style={styles.navLeft}>
              <div style={styles.navLogo}>🔧</div>
              <h1 style={styles.navTitle}>Customer Portal</h1>
            </div>
          </div>
        </nav>
        <main style={styles.main}>
          <div style={styles.errorContainer}>
            <div style={styles.errorIcon}>🔒</div>
            <h2 style={styles.errorTitle}>Please Log In</h2>
            <p style={styles.errorMessage}>You need to log in to view this invoice.</p>
            <Link to="/portal/login" style={styles.loginButton}>
              Go to Login
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (loading && !invoice) {
    return (
      <div style={styles.container}>
        <nav style={styles.navbar}>
          <div style={styles.navContent}>
            <div style={styles.navLeft}>
              <div style={styles.navLogo}>🔧</div>
              <h1 style={styles.navTitle}>Customer Portal</h1>
            </div>
            <button onClick={handleLogout} style={styles.logoutButton}>
              Logout
            </button>
          </div>
        </nav>
        <main style={styles.main}>
          <div style={styles.loadingState}>
            <div style={styles.spinner}></div>
            <p>Loading invoice...</p>
          </div>
        </main>
      </div>
    );
  }

  if (error && !invoice) {
    return (
      <div style={styles.container}>
        <nav style={styles.navbar}>
          <div style={styles.navContent}>
            <div style={styles.navLeft}>
              <div style={styles.navLogo}>🔧</div>
              <h1 style={styles.navTitle}>Customer Portal</h1>
            </div>
            <button onClick={handleLogout} style={styles.logoutButton}>
              Logout
            </button>
          </div>
        </nav>
        <main style={styles.main}>
          <div style={styles.errorContainer}>
            <div style={styles.errorIcon}>⚠️</div>
            <h2 style={styles.errorTitle}>Error Loading Invoice</h2>
            <p style={styles.errorMessage}>{error}</p>
            <Link to="/portal/dashboard" style={styles.backButton}>
              ← Back to Dashboard
            </Link>
          </div>
        </main>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div style={styles.container}>
        <nav style={styles.navbar}>
          <div style={styles.navContent}>
            <div style={styles.navLeft}>
              <div style={styles.navLogo}>🔧</div>
              <h1 style={styles.navTitle}>Customer Portal</h1>
            </div>
            <button onClick={handleLogout} style={styles.logoutButton}>
              Logout
            </button>
          </div>
        </nav>
        <main style={styles.main}>
          <div style={styles.errorContainer}>
            <div style={styles.errorIcon}>📄</div>
            <h2 style={styles.errorTitle}>Invoice Not Found</h2>
            <Link to="/portal/dashboard" style={styles.backButton}>
              ← Back to Dashboard
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const items = invoice.items || [];

  return (
    <div style={styles.container}>
      {/* Navigation */}
      <nav style={styles.navbar}>
        <div style={styles.navContent}>
          <div style={styles.navLeft}>
            <div style={styles.navLogo}>🔧</div>
            <h1 style={styles.navTitle}>Customer Portal</h1>
          </div>
          <button onClick={handleLogout} style={styles.logoutButton}>
            Logout
          </button>
        </div>
      </nav>

      {/* Main Content */}
      <main style={styles.main}>
        <div style={styles.content}>
          {/* Breadcrumb */}
          <div style={styles.breadcrumb}>
            <Link to="/portal/dashboard" style={styles.breadcrumbLink}>Dashboard</Link>
            <span style={styles.breadcrumbSeparator}>/</span>
            <Link to="/portal/invoices" style={styles.breadcrumbLink}>Invoices</Link>
            <span style={styles.breadcrumbSeparator}>/</span>
            <span style={styles.breadcrumbCurrent}>{invoice.invoice_number}</span>
          </div>

          {/* Success Message */}
          {paymentSuccess && (
            <div style={styles.successBanner}>
              <span style={styles.successIcon}>✓</span>
              <div>
                <strong>Payment Successful!</strong>
                <p style={styles.successSubtext}>Thank you for your payment. A receipt has been sent to your email.</p>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div style={styles.errorBanner}>
              <span>⚠️</span> {error}
            </div>
          )}

          {/* Invoice Header */}
          <div style={styles.invoiceHeader}>
            <div>
              <h2 style={styles.invoiceTitle}>{invoice.invoice_number}</h2>
              <p style={styles.invoiceSubtitle}>
                Issued: {formatDate(invoice.date)} · Due: {formatDate(invoice.due_date)}
              </p>
            </div>
            <div style={styles.invoiceStatus}>
              <span style={{
                ...styles.statusBadge,
                background: `${getStatusColor(invoice.status)}20`,
                color: getStatusColor(invoice.status)
              }}>
                {invoice.status === 'paid' ? '✓ Paid' :
                 invoice.status === 'overdue' ? '⚠ Overdue' :
                 invoice.status === 'pending' ? '⏳ Pending' :
                 invoice.status}
              </span>
              {invoice.paid_date && (
                <p style={styles.paidDate}>Paid on {formatDate(invoice.paid_date)}</p>
              )}
            </div>
          </div>

          {/* Payment Modal */}
          {showPayment && clientSecret && (
            <div style={styles.paymentModal}>
              <div style={styles.paymentContent}>
                <div style={styles.paymentHeader}>
                  <h3 style={styles.paymentTitle}>Pay Invoice</h3>
                  <p style={styles.paymentSubtitle}>{invoice.invoice_number}</p>
                </div>
                {getStripe() ? (
                  <Elements stripe={getStripe()} options={{ clientSecret }}>
                    <CheckoutForm
                      invoice={invoice}
                      clientSecret={clientSecret}
                      onSuccess={handlePaymentSuccess}
                      onCancel={() => { setShowPayment(false); setClientSecret(null); }}
                    />
                  </Elements>
                ) : (
                  <div style={styles.paymentError}>
                    <p>Stripe is not configured. Please contact support to pay this invoice.</p>
                    <button onClick={() => { setShowPayment(false); setClientSecret(null); }} style={styles.cancelButton}>
                      Close
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Invoice Content - Modern Minimal Style */}
          <div style={styles.invoicePaper}>
            {/* Invoice Header */}
            <div style={styles.invoiceHeaderSection}>
              <div style={styles.invoiceHeaderLeft}>
                <h2 style={styles.invoiceTitleBig}>INVOICE</h2>
                <p style={styles.invoiceNumberBig}>{invoice.invoice_number}</p>
              </div>
              <div style={styles.invoiceHeaderRight}>
                <div style={styles.invoiceMetaRow}>
                  <span style={styles.invoiceMetaLabel}>Date:</span>
                  <span style={styles.invoiceMetaValue}>{formatDate(invoice.date)}</span>
                </div>
                <div style={styles.invoiceMetaRow}>
                  <span style={styles.invoiceMetaLabel}>Due Date:</span>
                  <span style={styles.invoiceMetaValue}>{formatDate(invoice.due_date)}</span>
                </div>
                <div style={styles.invoiceMetaRow}>
                  <span style={styles.invoiceMetaLabel}>Status:</span>
                  <span style={{
                    ...styles.invoiceStatusBadge,
                    background: `${getStatusColor(invoice.status)}20`,
                    color: getStatusColor(invoice.status)
                  }}>
                    {invoice.status === 'paid' ? '✓ Paid' :
                     invoice.status === 'overdue' ? '⚠ Overdue' :
                     invoice.status === 'pending' ? '⏳ Pending' :
                     invoice.status}
                  </span>
                </div>
              </div>
            </div>

            {/* BILL TO / FROM Section */}
            <div style={styles.billSection}>
              <div style={styles.billBox}>
                <p style={styles.billLabel}>BILL TO</p>
                <p style={styles.billName}>{invoice.customer_name}</p>
                {invoice.customer_email && (
                  <p style={styles.billDetail}>{invoice.customer_email}</p>
                )}
              </div>
              <div style={styles.billBox}>
                <p style={styles.billLabel}>FROM</p>
                <p style={styles.billName}>TradeLynx</p>
                <p style={styles.billDetail}>Your trusted service provider</p>
              </div>
            </div>

            {/* Job Information */}
            {invoice.jobs && (
              <div style={styles.jobInfoBox}>
                <p style={styles.billLabel}>JOB DETAILS</p>
                <p style={styles.jobInfoText}>
                  {invoice.jobs.job_type} at {invoice.jobs.address}
                </p>
                {invoice.jobs.scheduled_date && (
                  <p style={styles.jobInfoSub}>
                    Completed: {formatDate(invoice.jobs.scheduled_date)}
                  </p>
                )}
              </div>
            )}

            {/* Invoice Items Table */}
            <div style={styles.itemsContainer}>
              <table style={styles.invoiceTable}>
                <thead>
                  <tr>
                    <th style={styles.tableTh}>Item</th>
                    <th style={{...styles.tableTh, textAlign: 'center'}}>Quantity</th>
                    <th style={{...styles.tableTh, textAlign: 'right'}}>Price</th>
                    <th style={{...styles.tableTh, textAlign: 'right'}}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr>
                      <td colSpan="4" style={styles.tableTd}>
                        <span style={styles.emptyItem}>Invoice #{invoice.invoice_number}</span>
                      </td>
                    </tr>
                  ) : (
                    items.map((item, index) => (
                      <tr key={index}>
                        <td style={styles.tableTd}>{item.description || item.name}</td>
                        <td style={{...styles.tableTd, textAlign: 'center'}}>{item.quantity || 1}</td>
                        <td style={{...styles.tableTd, textAlign: 'right'}}>{formatCurrency(item.price || item.unit_price)}</td>
                        <td style={{...styles.tableTd, textAlign: 'right', fontWeight: '500'}}>
                          {formatCurrency((item.quantity || 0) * (item.price || item.unit_price || 0))}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Notes */}
            {invoice.notes && (
              <div style={styles.notesBox}>
                <p style={styles.billLabel}>Notes</p>
                <p style={styles.notesText}>{invoice.notes}</p>
              </div>
            )}

            {/* Total Section */}
            <div style={styles.totalBox}>
              <div style={styles.totalRowStyled}>
                <span style={styles.totalLabelStyled}>Total</span>
                <span style={styles.totalValueStyled}>
                  {formatCurrency(invoice.total || invoice.amount)}
                </span>
              </div>
              {invoice.status === 'paid' && invoice.paid_date && (
                <p style={styles.paidDateText}>
                  Paid on {formatDate(invoice.paid_date)}
                </p>
              )}
            </div>

            {/* Pay Button */}
            {invoice.status !== 'paid' && !showPayment && (
              <div style={styles.paySectionStyled}>
                <button
                  onClick={handlePaymentClick}
                  disabled={loading}
                  style={{
                    ...styles.payInvoiceButtonStyled,
                    ...(loading ? styles.payButtonDisabled : {})
                  }}
                >
                  {loading ? (
                    <>
                      <span style={styles.buttonSpinner}></span>
                      Loading...
                    </>
                  ) : (
                    `Pay ${formatCurrency(invoice.total || invoice.amount)}`
                  )}
                </button>
                <p style={styles.secureNoteStyled}>🔒 Secure payment powered by Stripe</p>
              </div>
            )}
          </div>

          {/* Back Link */}
          <Link to="/portal/dashboard" style={styles.backLink}>
            ← Back to Dashboard
          </Link>
        </div>
      </main>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    background: '#f3f4f6',
    display: 'flex',
    flexDirection: 'column'
  },
  navbar: {
    background: 'white',
    borderBottom: '1px solid #e5e7eb',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    width: '100%'
  },
  navContent: {
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '16px 24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%'
  },
  navLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px'
  },
  navLogo: {
    fontSize: '24px'
  },
  navTitle: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827',
    margin: 0
  },
  logoutButton: {
    background: 'transparent',
    border: '1px solid #d1d5db',
    color: '#6b7280',
    padding: '8px 16px',
    borderRadius: '6px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  main: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
    minHeight: 'calc(100vh - 73px)',
    padding: '32px 24px'
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    width: '100%',
    maxWidth: '800px'
  },
  loadingState: {
    padding: '60px',
    textAlign: 'center',
    color: '#6b7280'
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid #e5e7eb',
    borderTopColor: '#f97316',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite',
    margin: '0 auto 16px'
  },
  errorContainer: {
    textAlign: 'center',
    padding: '60px 20px',
    background: 'white',
    borderRadius: '12px'
  },
  errorIcon: {
    fontSize: '48px',
    marginBottom: '16px'
  },
  errorTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 8px 0'
  },
  errorMessage: {
    color: '#6b7280',
    margin: '0 0 24px 0'
  },
  backButton: {
    display: 'inline-block',
    color: '#f97316',
    textDecoration: 'none',
    fontWeight: '500'
  },
  loginButton: {
    display: 'inline-block',
    background: '#f97316',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '8px',
    textDecoration: 'none',
    fontWeight: '600',
    marginTop: '16px'
  },
  breadcrumb: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px'
  },
  breadcrumbLink: {
    color: '#6b7280',
    textDecoration: 'none'
  },
  breadcrumbSeparator: {
    color: '#d1d5db'
  },
  breadcrumbCurrent: {
    color: '#111827',
    fontWeight: '500'
  },
  successBanner: {
    background: '#dcfce7',
    border: '1px solid #86efac',
    borderRadius: '8px',
    padding: '16px 20px',
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px'
  },
  successIcon: {
    width: '24px',
    height: '24px',
    background: '#22c55e',
    color: 'white',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    flexShrink: 0
  },
  successSubtext: {
    margin: '4px 0 0 0',
    fontSize: '14px',
    color: '#166534'
  },
  errorBanner: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '8px',
    padding: '12px 16px',
    color: '#dc2626',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  invoiceHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    flexWrap: 'wrap',
    gap: '16px'
  },
  invoiceTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827',
    margin: '0 0 4px 0'
  },
  invoiceSubtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0
  },
  invoiceStatus: {
    textAlign: 'right'
  },
  statusBadge: {
    display: 'inline-block',
    padding: '6px 14px',
    borderRadius: '9999px',
    fontSize: '14px',
    fontWeight: '600'
  },
  paidDate: {
    fontSize: '13px',
    color: '#6b7280',
    margin: '6px 0 0 0'
  },
  invoiceCard: {
    background: 'white',
    borderRadius: '12px',
    padding: '32px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
  },
  billToSection: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid #e5e7eb'
  },
  sectionLabel: {
    fontSize: '12px',
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    margin: '0 0 8px 0'
  },
  customerName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 4px 0'
  },
  customerEmail: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0
  },
  jobSection: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid #e5e7eb'
  },
  jobInfo: {
    fontSize: '14px',
    color: '#374151',
    margin: '0 0 4px 0'
  },
  jobDate: {
    fontSize: '13px',
    color: '#9ca3af',
    margin: 0
  },
  itemsSection: {
    marginBottom: '24px'
  },
  itemsTable: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  tableHeader: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr',
    gap: '16px',
    padding: '12px 0',
    borderBottom: '2px solid #e5e7eb',
    fontSize: '12px',
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  tableRow: {
    display: 'grid',
    gridTemplateColumns: '2fr 1fr 1fr 1fr',
    gap: '16px',
    padding: '12px 0',
    borderBottom: '1px solid #f3f4f6',
    fontSize: '14px',
    color: '#374151',
    alignItems: 'center'
  },
  descriptionCol: {
    color: '#111827'
  },
  quantityCol: {
    textAlign: 'center'
  },
  priceCol: {
    textAlign: 'right'
  },
  amountCol: {
    textAlign: 'right',
    fontWeight: '500'
  },
  emptyItem: {
    color: '#9ca3af',
    fontStyle: 'italic'
  },
  notesSection: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: '1px solid #e5e7eb'
  },
  notesText: {
    fontSize: '14px',
    color: '#374151',
    lineHeight: '1.5',
    margin: 0,
    whiteSpace: 'pre-wrap'
  },
  totalSection: {
    marginBottom: '24px'
  },
  totalRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 0'
  },
  totalLabel: {
    fontSize: '18px',
    fontWeight: '600',
    color: '#111827'
  },
  totalValue: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#111827'
  },
  paySection: {
    textAlign: 'center',
    paddingTop: '8px'
  },
  payInvoiceButton: {
    background: '#f97316',
    color: 'white',
    border: 'none',
    padding: '16px 48px',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px'
  },
  payButtonDisabled: {
    background: '#9ca3af',
    cursor: 'not-allowed'
  },
  buttonSpinner: {
    width: '18px',
    height: '18px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTopColor: 'white',
    borderRadius: '50%',
    animation: 'spin 0.8s linear infinite'
  },
  secureNote: {
    fontSize: '13px',
    color: '#9ca3af',
    margin: '12px 0 0 0'
  },
  backLink: {
    color: '#6b7280',
    textDecoration: 'none',
    fontSize: '14px',
    fontWeight: '500'
  },

  // Modern Invoice Styles
  invoicePaper: {
    background: 'white',
    borderRadius: '8px',
    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -1px rgba(0,0,0,0.06)',
    overflow: 'hidden',
    width: '100%',
    maxWidth: '800px',
    margin: '0 auto'
  },
  invoiceHeaderSection: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '40px',
    borderBottom: '1px solid #e5e7eb',
    background: '#fafafa'
  },
  invoiceHeaderLeft: {},
  invoiceHeaderRight: {
    textAlign: 'right'
  },
  invoiceTitleBig: {
    fontSize: '32px',
    fontWeight: '700',
    color: '#111827',
    margin: '0 0 8px 0',
    letterSpacing: '2px'
  },
  invoiceNumberBig: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0
  },
  invoiceMetaRow: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '8px'
  },
  invoiceMetaLabel: {
    fontSize: '13px',
    color: '#9ca3af',
    fontWeight: '500'
  },
  invoiceMetaValue: {
    fontSize: '14px',
    color: '#374151',
    fontWeight: '500',
    minWidth: '100px'
  },
  invoiceStatusBadge: {
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '9999px',
    fontSize: '12px',
    fontWeight: '600',
    textTransform: 'capitalize'
  },
  billSection: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '40px',
    padding: '32px 40px',
    borderBottom: '1px solid #e5e7eb'
  },
  billBox: {},
  billLabel: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    margin: '0 0 12px 0'
  },
  billName: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 4px 0'
  },
  billDetail: {
    fontSize: '14px',
    color: '#6b7280',
    margin: '0 0 2px 0'
  },
  jobInfoBox: {
    padding: '24px 40px',
    borderBottom: '1px solid #e5e7eb',
    background: '#fafafa'
  },
  jobInfoText: {
    fontSize: '14px',
    color: '#374151',
    margin: '0 0 4px 0'
  },
  jobInfoSub: {
    fontSize: '13px',
    color: '#9ca3af',
    margin: 0
  },
  itemsContainer: {
    padding: '32px 40px'
  },
  invoiceTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '14px'
  },
  tableTh: {
    padding: '12px 0',
    borderBottom: '2px solid #e5e7eb',
    textAlign: 'left',
    fontSize: '11px',
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  tableTd: {
    padding: '16px 0',
    borderBottom: '1px solid #f3f4f6',
    color: '#374151'
  },
  emptyItem: {
    color: '#9ca3af',
    fontStyle: 'italic'
  },
  notesBox: {
    padding: '24px 40px',
    borderTop: '1px solid #e5e7eb',
    background: '#fafafa'
  },
  notesText: {
    fontSize: '14px',
    color: '#374151',
    lineHeight: '1.6',
    margin: 0
  },
  totalBox: {
    padding: '32px 40px',
    borderTop: '2px solid #e5e7eb',
    background: '#111827'
  },
  totalRowStyled: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '8px'
  },
  totalLabelStyled: {
    fontSize: '16px',
    fontWeight: '600',
    color: '#9ca3af',
    textTransform: 'uppercase',
    letterSpacing: '1px'
  },
  totalValueStyled: {
    fontSize: '32px',
    fontWeight: '700',
    color: 'white'
  },
  paidDateText: {
    fontSize: '13px',
    color: '#22c55e',
    margin: '8px 0 0 0',
    textAlign: 'right'
  },
  paySectionStyled: {
    padding: '32px 40px',
    textAlign: 'center',
    background: '#fafafa',
    borderTop: '1px solid #e5e7eb'
  },
  payInvoiceButtonStyled: {
    background: '#f97316',
    color: 'white',
    border: 'none',
    padding: '16px 48px',
    borderRadius: '8px',
    fontSize: '18px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background 0.2s, transform 0.1s',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 4px 6px -1px rgba(249, 115, 22, 0.3)'
  },
  secureNoteStyled: {
    fontSize: '13px',
    color: '#9ca3af',
    margin: '16px 0 0 0'
  },

  paymentModal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '24px'
  },
  paymentContent: {
    background: 'white',
    borderRadius: '12px',
    padding: '32px',
    maxWidth: '480px',
    width: '100%',
    maxHeight: '90vh',
    overflow: 'auto'
  },
  paymentHeader: {
    textAlign: 'center',
    marginBottom: '24px'
  },
  paymentTitle: {
    fontSize: '20px',
    fontWeight: '600',
    color: '#111827',
    margin: '0 0 4px 0'
  },
  paymentSubtitle: {
    fontSize: '14px',
    color: '#6b7280',
    margin: 0
  },
  paymentForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px'
  },
  paymentError: {
    background: '#fef2f2',
    border: '1px solid #fecaca',
    borderRadius: '6px',
    padding: '12px',
    color: '#dc2626',
    fontSize: '14px'
  },
  paymentButtons: {
    display: 'flex',
    gap: '12px',
    marginTop: '8px'
  },
  cancelButton: {
    flex: 1,
    padding: '12px',
    border: '1px solid #d1d5db',
    background: 'white',
    color: '#6b7280',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer'
  },
  payButton: {
    flex: 2,
    padding: '12px',
    background: '#f97316',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px'
  }
};

export default PortalInvoiceDetail;
